/*
  /women の独自アイコンの描き方「B 手の揺れの太線」の土台 (2026-10-12 オーナーが 3 案から選んだ。「普通の icon を使うのはやめよう。独自SVG を作るんだ」)。
  決まりは次の通り。

  1. 24 の箱、線の太さ 2.6、端と角は丸。部品どうしは線の外側で 1.2 以上あける。1 つのアイコンの線は 5 本まで
  2. 直線は中点で線と垂直に 0.8 ふくらませ、向きは辺ごとに交互 (8 より短い辺は長さに比べて小さく)。角は半径 2.5、細かい角は 1。
     円は 8 度回し、4 つの弧の半径を交互に 0.2〜0.3 増減させる
  3. 色は線の色 (currentColor) と黄の 2 つ。黄は閉じた形 1 つだけを線で囲んで塗る。黄の上に乗る印は #0E0E10

  黄は --ic-mark (無ければ HP の --yellow、それも無ければ #FFE566)。プランの札は --ic-mark にプランの色を入れて塗りを替える。
  黄の地のボタンの中では、置く側が --icon-fill を白にする (地と同じ黄だと塗りが溶けて形が読めない)。--icon-fill は親から継ぎ、塗りの色の中でいちばん強い。
  形の計算は乱数も時刻も使わない。サーバーとブラウザで同じ d になり、hydration で食い違わない
*/
import type { CSSProperties, ReactNode } from "react";

const MARK = "var(--icon-fill, var(--ic-mark, var(--yellow, #FFE566)))";
const ON = "var(--ic-on, #0E0E10)";

/** 1 本の線。文字列だけなら線の色で引く線。role で塗りと色を替える */
export type HandPart =
  | string
  | {
      d: string;
      /** mark 黄で塗って線で囲む、markFill 黄の塗りだけ、on 黄の上の墨の線、onFill 黄の上の墨の塗り、ink 線の色の塗り */
      role: "mark" | "markFill" | "on" | "onFill" | "ink" | "line";
      w?: number;
      dash?: string;
    };

const r2 = (n: number) => Math.round(n * 100) / 100;
type Pt = readonly [number, number] | readonly [number, number, number];

/**
 * 揺れの付いた折れ線。各点の 3 つ目の数はその角の丸みの半径 (既定は r)。
 * 直線は中点で線と垂直に w だけふくらませ、向きは辺ごとに交互にする。8 より短い辺は長さに比例して小さく
 */
export function wp(pts: readonly Pt[], { closed = true, r = 2.5, w = 0.8 }: { closed?: boolean; r?: number; w?: number } = {}): string {
  const P = pts.map((p) => ({ x: p[0], y: p[1], r: p[2] ?? r }));
  const n = P.length;
  const toward = (a: { x: number; y: number }, b: { x: number; y: number }, d: number) => {
    const L = Math.hypot(b.x - a.x, b.y - a.y);
    const k = Math.min(d, L / 2.2) / L;
    return { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k };
  };
  const seg = (S: { x: number; y: number }, E: { x: number; y: number }, i: number) => {
    const L = Math.hypot(E.x - S.x, E.y - S.y) || 1;
    const ww = w * Math.min(1, L / 8) * (i % 2 ? 1 : -1);
    const cx = (S.x + E.x) / 2 + (-(E.y - S.y) / L) * ww;
    const cy = (S.y + E.y) / 2 + ((E.x - S.x) / L) * ww;
    return `Q${r2(cx)} ${r2(cy)} ${r2(E.x)} ${r2(E.y)}`;
  };
  if (closed) {
    const a = P.map((p, i) => toward(p, P[(i - 1 + n) % n], p.r));
    const b = P.map((p, i) => toward(p, P[(i + 1) % n], p.r));
    let d = `M${r2(b[0].x)} ${r2(b[0].y)}`;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      d += seg(b[i], a[j], i);
      d += `Q${r2(P[j].x)} ${r2(P[j].y)} ${r2(b[j].x)} ${r2(b[j].y)}`;
    }
    return `${d}Z`;
  }
  let d = `M${r2(P[0].x)} ${r2(P[0].y)}`;
  let cur = { x: P[0].x, y: P[0].y };
  for (let i = 0; i < n - 1; i++) {
    const j = i + 1;
    const last = j === n - 1;
    const end = last ? P[j] : toward(P[j], P[i], P[j].r);
    d += seg(cur, end, i);
    if (!last) {
      const nb = toward(P[j], P[j + 1], P[j].r);
      d += `Q${r2(P[j].x)} ${r2(P[j].y)} ${r2(nb.x)} ${r2(nb.y)}`;
      cur = nb;
    }
  }
  return d;
}

/** 揺れの付いた直線 (折れ線の 2 点版) */
export const ln = (x1: number, y1: number, x2: number, y2: number, w = 0.8) =>
  wp(
    [
      [x1, y1],
      [x2, y2],
    ],
    { closed: false, w },
  );

/** 揺れの付いた角丸の四角 */
export const box = (x: number, y: number, w: number, h: number, r = 2.5) =>
  wp(
    [
      [x, y],
      [x + w, y],
      [x + w, y + h],
      [x, y + h],
    ],
    { r },
  );

/** 揺れの付いた楕円。8 度回し、4 つの弧の半径を交互に増減させる */
export function we(cx: number, cy: number, rx: number, ry: number = rx, rot = 8): string {
  // 小さい円ほど揺れも小さくする (半径 3 未満は比例)
  const k = Math.min(1, Math.min(rx, ry) / 3);
  const ds = [0.3 * k, -0.2 * k, 0.15 * k, -0.3 * k];
  const th = (rot * Math.PI) / 180;
  const R = (x: number, y: number) => ({ x: cx + x * Math.cos(th) - y * Math.sin(th), y: cy + x * Math.sin(th) + y * Math.cos(th) });
  const at = (q: number) => {
    const t = (q * Math.PI) / 2;
    const ax = rx + ds[q % 4];
    const ay = ry + ds[q % 4];
    return { x: ax * Math.cos(t), y: ay * Math.sin(t), tx: -ax * Math.sin(t), ty: ay * Math.cos(t) };
  };
  const K = 0.5523;
  let d = "";
  for (let q = 0; q < 4; q++) {
    const A = at(q);
    const B = at(q + 1);
    const p0 = R(A.x, A.y);
    const c1 = R(A.x + A.tx * K, A.y + A.ty * K);
    const c2 = R(B.x - B.tx * K, B.y - B.ty * K);
    const p1 = R(B.x, B.y);
    if (q === 0) d += `M${r2(p0.x)} ${r2(p0.y)}`;
    d += `C${r2(c1.x)} ${r2(c1.y)} ${r2(c2.x)} ${r2(c2.y)} ${r2(p1.x)} ${r2(p1.y)}`;
  }
  return `${d}Z`;
}

/* 部品の書き方を短くする。mark(d) は黄の面、on(d) は黄の上の墨の線 */
export const mark = (d: string): HandPart => ({ d, role: "mark" });
export const markFill = (d: string): HandPart => ({ d, role: "markFill" });
export const on = (d: string, w?: number): HandPart => ({ d, role: "on", w });
export const onFill = (d: string): HandPart => ({ d, role: "onFill" });
export const ink = (d: string): HandPart => ({ d, role: "ink" });
export const line = (d: string, w?: number, dash?: string): HandPart => ({ d, role: "line", w, dash });

function Part({ part }: { part: HandPart }) {
  if (typeof part === "string") return <path d={part} />;
  const { d, role, w, dash } = part;
  switch (role) {
    case "mark":
      return <path d={d} fill={MARK} />;
    case "markFill":
      return <path d={d} fill={MARK} stroke="none" />;
    case "on":
      return <path d={d} stroke={ON} strokeWidth={w} />;
    case "onFill":
      return <path d={d} fill={ON} stroke="none" />;
    case "ink":
      return <path d={d} fill="currentColor" stroke="none" />;
    default:
      return <path d={d} strokeWidth={w} strokeDasharray={dash} />;
  }
}

/** B の描き方の svg。色は親の字の色 (currentColor) を継ぎ、黄は --ic-mark */
export function HandSvg({
  parts,
  size = 24,
  className,
  style,
  children,
  ...rest
}: {
  parts?: readonly HandPart[];
  size?: number;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  [data: `data-${string}`]: string | undefined;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
      style={style}
      {...rest}
    >
      {parts?.map((p, i) => <Part key={i} part={p} />)}
      {children}
    </svg>
  );
}
